import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Compass, PenTool, CheckSquare, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = { Compass, PenTool, CheckSquare, Wrench, Building2 };

const Home = () => {
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, productsRes, servicesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects`),
        axios.get(`${BACKEND_URL}/api/products`),
        axios.get(`${BACKEND_URL}/api/services`),
      ]);
      setProjects(projectsRes.data);
      setProducts(productsRes.data);
      setServices(servicesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const featuredProjects = projects.slice(0, 3);
  const featuredProducts = products.filter(p => p.featured).slice(0, 3);
  // First 3 services from the same source as the public Services page (sorted by displayOrder)
  const featuredServices = services.slice(0, 3);

  const renderServiceIcon = (icon) => {
    if (!icon) return <Building2 className="h-7 w-7" style={{ color: '#00CED1' }} />;
    const Icon = iconMap[icon];
    if (Icon) return <Icon className="h-7 w-7" style={{ color: '#00CED1' }} />;
    return <span className="text-3xl leading-none">{icon}</span>;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[90vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://customer-assets.emergentagent.com/job_arch-furniture-hub/artifacts/qv2wfba7_BAP-Home.jpg')`
        }}
      >
        <div className="text-center px-4 max-w-4xl mx-auto z-10">
          <Badge className="mb-6 px-4 py-2 text-sm" style={{ backgroundColor: '#00CED1', color: 'white' }}>
            Award-Winning Architecture & Design
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Building Dreams Into
            <span style={{ color: '#00CED1' }}> Reality</span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Transform your vision with innovative architectural solutions and premium custom furniture
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="text-white font-semibold px-8 py-6 text-base hover:scale-105 transition-transform duration-300"
                style={{ backgroundColor: '#00CED1' }}
              >
                Start Your Project <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/gallery">
              <Button 
                size="lg" 
                variant="outline"
                className="font-semibold px-8 py-6 text-base border-2 bg-white/10 backdrop-blur-sm text-white hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                View Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span style={{ color: '#00CED1' }}>Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive architectural services tailored to bring your vision to life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center py-10">
                <p className="text-gray-600">Loading services…</p>
              </div>
            ) : featuredServices.length === 0 ? (
              <div className="col-span-3 text-center py-10">
                <p className="text-gray-600">No services published yet.</p>
                <Link to="/services">
                  <Button variant="link" style={{ color: '#00CED1' }}>Manage services →</Button>
                </Link>
              </div>
            ) : featuredServices.map((service) => {
              const imgSrc = service.image
                ? (service.image.startsWith('http') ? service.image : `${BACKEND_URL}${service.image}`)
                : null;
              return (
                <Link
                  to="/services"
                  key={service.id}
                  data-testid={`home-service-${service.id}`}
                >
                  <Card
                    className="group hover:shadow-2xl transition-all duration-500 border-0 overflow-hidden h-full"
                    style={{ boxShadow: '0 4px 20px rgba(0, 206, 209, 0.1)' }}
                  >
                    {imgSrc && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={cldPresets.serviceCard(imgSrc)}
                          alt={service.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: '#00CED115' }}
                      >
                        {renderServiceIcon(service.icon)}
                      </div>
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed line-clamp-4">{service.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                size="lg"
                className="font-semibold text-white hover:scale-105 transition-transform duration-300"
                style={{ backgroundColor: '#00CED1' }}
              >
                Explore All Services <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Featured <span style={{ color: '#00CED1' }}>Projects</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our portfolio of exceptional architectural achievements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center py-10">
                <p className="text-gray-600">Loading projects...</p>
              </div>
            ) : featuredProjects.length === 0 ? (
              <div className="col-span-3 text-center py-10">
                <p className="text-gray-600">No projects yet.</p>
              </div>
            ) : (
            featuredProjects.map((project) => {
              const projectImage = project.images && project.images.length > 0 ? project.images[0] : project.image;
              return (
              <Card 
                key={project.id}
                className="group overflow-hidden border-0 hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={cldPresets.homeFeature(projectImage.startsWith('http') ? projectImage : `${BACKEND_URL}${projectImage}`)}
                    alt={project.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge 
                      className="text-white capitalize"
                      style={{ backgroundColor: project.category === 'commercial' ? '#FF0000' : '#00CED1' }}
                    >
                      {project.category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{project.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{project.location}</span>
                    <span>{project.year}</span>
                  </div>
                </CardContent>
              </Card>
              );
            })
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/gallery">
              <Button 
                size="lg"
                className="font-semibold text-white hover:scale-105 transition-transform duration-300"
                style={{ backgroundColor: '#00CED1' }}
              >
                View Full Gallery <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Premium <span style={{ color: '#00CED1' }}>Furniture</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Curated collection of modern furniture and decor for your space
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProducts.map((product) => (
              <Card 
                key={product.id}
                className="group overflow-hidden border-0 hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden bg-gray-100">
                  <img 
                    src={cldPresets.productCard(product.image && product.image.startsWith('http') ? product.image : `${BACKEND_URL}${product.image || ''}`)}
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {product.featured && (
                    <Badge 
                      className="absolute top-4 left-4 text-white"
                      style={{ backgroundColor: '#FF0000' }}
                    >
                      Featured
                    </Badge>
                  )}
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold" style={{ color: '#00CED1' }}>
                      R{product.price.toFixed(2)}
                    </span>
                    <Link to={`/shop`}>
                      <Button 
                        size="sm"
                        className="text-white hover:scale-105 transition-transform duration-300"
                        style={{ backgroundColor: '#00CED1' }}
                      >
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop">
              <Button 
                size="lg"
                className="font-semibold text-white hover:scale-105 transition-transform duration-300"
                style={{ backgroundColor: '#00CED1' }}
              >
                Shop All Products <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 px-4 relative bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 206, 209, 0.9), rgba(0, 206, 209, 0.9)), url('https://images.unsplash.com/photo-1574848296471-28f79a036f79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzcwODQxNzY1fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Dream Project?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's collaborate to create something extraordinary. Our team is ready to bring your vision to life.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="bg-white font-semibold px-8 py-6 text-base hover:scale-105 transition-transform duration-300"
              style={{ color: '#00CED1' }}
            >
              Get In Touch <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
